import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useChatContext } from '../bot-core/store/ChatContext';
import { useBotContext } from './store/BotContext';
import { UIMessage } from '../bot-core/types/chat.types';
import SearchResultList from './search-results/SearchResultList';
import RoomOptionsList from './search-results/RoomOptionsList';
import PackageRoomOptionsList from './search-results/PackageRoomOptionsList';
import EventTicketOptionsList from './search-results/EventTicketOptionsList';
import ProductImageGallery from './ProductImageGallery';
import CartPreviewCard from './CartPreviewCard';
import { checkoutPayloadToCartPreview } from './utils/checkoutPayloadToCartPreview';
import { buildBotCheckoutDeepLink } from './utils/botCheckoutLink';

const TOOL_TO_PRODUCT: Record<
  string,
  'flight' | 'hotel' | 'package' | 'activity' | 'event'
> = {
  search_flights: 'flight',
  search_hotels: 'hotel',
  search_packages: 'package',
  search_attractions: 'activity',
  search_events: 'event',
};

export interface SendOpts {
  selection?: {
    book_hash?: string;
    package_room?: {
      room_id?: string | number;
      original_room_id?: string;
      room_selection_key?: string;
      adult?: number;
      child?: number;
      infant?: number;
    };
    event_ticket?: {
      ticket_id?: string | number;
      quantity?: number;
      delivery_method_id?: number;
    };
  };
}

interface BotMessageExtrasProps {
  msg: UIMessage;
  onSend: (text: string, opts?: SendOpts) => void;
}

/**
 * Rich UI rendered under an assistant turn: search-result cards, room/ticket
 * option lists, and the cart preview. Shared by BotWidget and DrawerBotWidget
 * so the two stay in lockstep.
 *
 * Option lists render for EVERY turn that produced them — not just the latest.
 * The active turn's list is interactive; once superseded it stays in the
 * transcript as a collapsed, view-only group (see CollapsibleResultGroup) the
 * user can expand to preview. The option the user picked is captured at
 * click-time (listSelection) so the collapsed header can name it.
 */
const BotMessageExtras: React.FC<BotMessageExtrasProps> = ({ msg, onSend }) => {
  const { state: chatState, dispatch: chatDispatch } = useChatContext();
  const { botState } = useBotContext();
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);

  const lastMsgId =
    chatState.messages[chatState.messages.length - 1]?.id;
  const lastCartPreviewMsgId = useMemo(() => {
    for (let i = chatState.messages.length - 1; i >= 0; i--) {
      if (chatState.messages[i].cartPreview) return chatState.messages[i].id;
    }
    return null;
  }, [chatState.messages]);

  // A list is interactive only while it's the active (latest, settled) turn.
  // Otherwise it's superseded → collapsed + view-only.
  const active = msg.id === lastMsgId && !chatState.isStreaming;
  const sel = msg.listSelection;

  const markSelection = (key: string | number, label: string) =>
    chatDispatch({
      type: 'SET_LIST_SELECTION',
      messageId: msg.id,
      selection: { key, label },
    });

  const extras: React.ReactNode[] = [];

  if (msg.searchResults) {
    const productType = TOOL_TO_PRODUCT[msg.searchResults.tool] ?? 'hotel';
    extras.push(
      <SearchResultList
        key="search-results"
        productType={productType}
        results={msg.searchResults.results}
        items={msg.searchResults.items}
        disabled={!active}
        selectedKey={typeof sel?.key === 'number' ? sel.key : undefined}
        pickLabel={sel?.label}
        onSelect={(index, displayName) => {
          markSelection(index, displayName);
          const needsDetailsFirst =
            productType === 'package' || productType === 'event';
          const message = needsDetailsFirst
            ? `Show me details for option ${index + 1} — ${displayName}`
            : `I'll take option ${index + 1} — ${displayName}`;
          onSend(message);
        }}
      />,
    );
  }

  // Hotel room options (view_product_details). Rendered whenever the sideband
  // carries hotel pricing; interactive only on the active turn.
  if (msg.productDetails?.raw?.pricing) {
    extras.push(
      <RoomOptionsList
        key="room-options"
        raw={msg.productDetails.raw}
        disabled={!active}
        selectedKey={typeof sel?.key === 'string' ? sel.key : undefined}
        pickLabel={sel?.label}
        onSelect={(bookHash, friendlyText, label) => {
          markSelection(bookHash, label);
          onSend(friendlyText, { selection: { book_hash: bookHash } });
        }}
      />,
    );
  }

  const packageInfoRaw = msg.productDetails?.raw?.packageInfo ?? null;
  if (packageInfoRaw?.price?.length) {
    extras.push(
      <PackageRoomOptionsList
        key="package-room-options"
        raw={msg.productDetails!.raw}
        disabled={!active}
        selectedKey={typeof sel?.key === 'string' ? sel.key : undefined}
        pickLabel={sel?.label}
        onSelect={(selection, friendlyText, label) => {
          markSelection(selection.room_selection_key, label);
          onSend(friendlyText, { selection: { package_room: selection } });
        }}
      />,
    );
  }

  const eventTicketsRaw = msg.productDetails?.raw?.tickets;
  if (Array.isArray(eventTicketsRaw) && eventTicketsRaw.length > 0) {
    extras.push(
      <EventTicketOptionsList
        key="event-ticket-options"
        raw={msg.productDetails!.raw}
        disabled={!active}
        pickLabel={sel?.label}
        onSelect={(selection, friendlyText, label) => {
          markSelection(selection.ticket_id, label);
          onSend(friendlyText, { selection: { event_ticket: selection } });
        }}
      />,
    );
  }

  // Hotel/room photos (show_product_images). Rendered on every turn that
  // produced them, view-only — no selection interaction.
  if (msg.productImages) {
    extras.push(
      <ProductImageGallery key="product-images" data={msg.productImages} />,
    );
  }

  if (msg.cartPreview && !chatState.isStreaming) {
    const preview =
      msg.id === lastCartPreviewMsgId && botState.checkoutPayload
        ? checkoutPayloadToCartPreview(botState.checkoutPayload)
        : msg.cartPreview;
    extras.push(<CartPreviewCard key="cart-preview" cartPreview={preview} />);
  }

  // Deterministic checkout link rendered straight from the generate_checkout_url
  // sideband — NOT scraped from the LLM's prose (the model is told not to write
  // a link, so a prose URL was unreliable). Renders the full URL as a real
  // anchor that opens checkout in a NEW TAB, alongside the "Complete booking"
  // CTA + cart chips. Tokenless same-session deep-link → useBotSessionHydration.
  if (msg.checkoutReady?.ready && !chatState.isStreaming) {
    const url = buildBotCheckoutDeepLink(msg.checkoutReady.session_id);
    extras.push(
      <div key="checkout-link" className="bot-checkout-link">
        {t?.checkout?.openInNewTab && (
          <span className="bot-checkout-link__label">
            {t.checkout.openInNewTab}
          </span>
        )}
        <a
          className="bot-checkout-link__url"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      </div>,
    );
  }

  return extras.length ? <>{extras}</> : null;
};

export default BotMessageExtras;
